import { Button, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { LeadProfile } from "api/resources/lead/types"
import LoadingOverlay from "components/shared/LoadingOverlay"
import { createContext, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { routes } from "utils/routes"

type Context = {
  leadProfile: LeadProfile | null
  refreshData: () => void
  loading: boolean
}

const UserLeadContext = createContext<Context>({
  leadProfile: null,
  refreshData: () => {},
  loading: false,
})

export const UserLeadProvider = ({
  suraasaLeadId,
  children,
}: {
  children: React.ReactNode
  suraasaLeadId: string | undefined
}) => {
  const navigate = useNavigate()

  const profile = useQuery({
    queryFn: () =>
      api.lead.getLeadData({
        data: {
          suraasaLeadId,
        },
      }),
    queryKey: ["profile", suraasaLeadId],
  })

  return (
    <UserLeadContext.Provider
      value={{
        leadProfile: profile.data || null,
        refreshData: profile.refetch,
        loading: profile.isLoading,
      }}
    >
      {profile.isSuccess && children}
      {profile.isLoading && <LoadingOverlay />}
      {profile.isError && (
        <div className="flex h-screen flex-col items-center justify-center gap-2">
          <Typography variant="largeBody">
            We&apos;re unable to fetch data for this ID. Please that ensure the
            ID is correct.
          </Typography>

          <Button
            variant="text"
            onClick={() => {
              navigate(routes.home)
            }}
          >
            Click here to go back
          </Button>
        </div>
      )}
    </UserLeadContext.Provider>
  )
}

export const useUserLeadContext = () => useContext(UserLeadContext)
